import React from 'react';
import { Tabs } from '@/components/Tabs/Tabs';
import { TabDataItem } from '@/components/Tabs/Tabs.types';
import {
  RaportHSPSpecsProps,
  RaportHSPSpecsData,
} from './RaportHSPSpecs.types';

/**
 * Funkcja pomocnicza do konwersji danych specyfikacji na format TabDataItem
 */
const convertSpecsToTabData = (data: RaportHSPSpecsData): TabDataItem[] => {
  const tabData: TabDataItem[] = [];

  // Silnik i osiągi
  Object.entries(data.engine).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      engineType: 'Typ silnika',
      engineDesignation: 'Oznaczenie silnika',
      engineCapacity: 'Pojemność silnika',
      power: 'Moc',
      torque: 'Moment obrotowy',
      fuelType: 'Rodzaj paliwa',
      cylindersCount: 'Liczba cylindrów',
      valvesPerCylinder: 'Liczba zaworów na cylinder',
      compressionRatio: 'Stopień sprężania',
      turbocharger: 'Doładowanie',
    };

    tabData.push({
      title: 'Silnik i osiągi',
      label: labelMap[key] || key,
      icon: 'handshake',
      value: value || 'Opis właściwości',
    });
  });

  // Układ napędowy
  Object.entries(data.driveTrain).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      driveType: 'Typ napędu',
      gearboxType: 'Rodzaj skrzyni biegów',
      gearsCount: 'Liczba biegów',
      clutchType: 'Typ sprzęgła',
      differentialType: 'Typ dyferencjału',
      transferCase: 'Skrzynka rozdzielcza',
    };

    tabData.push({
      title: 'Układ napędowy',
      label: labelMap[key] || key,
      icon: 'network-nodes',
      value: value || 'Opis właściwości',
    });
  });

  // Nadwozie
  Object.entries(data.body).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      bodyType: 'Typ nadwozia',
      doorsCount: 'Liczba drzwi',
      seatsCount: 'Liczba miejsc',
      trunkCapacity: 'Pojemność bagażnika',
      fuelTankCapacity: 'Pojemność zbiornika paliwa',
      dragCoefficient: 'Współczynnik oporu powietrza',
      paintType: 'Typ lakieru',
    };

    tabData.push({
      title: 'Nadwozie',
      icon: 'car',
      label: labelMap[key] || key,
      value: value || 'Opis właściwości',
    });
  });

  // Wymiary i masa
  Object.entries(data.dimensions).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      length: 'Długość',
      width: 'Szerokość',
      height: 'Wysokość',
      wheelbase: 'Rozstaw osi',
      frontTrack: 'Rozstaw kół przednich',
      rearTrack: 'Rozstaw kół tylnych',
      groundClearance: 'Prześwit',
      curbWeight: 'Masa własna',
      maxWeight: 'Masa całkowita',
      towingCapacity: 'Maksymalna masa przyczepy',
    };

    tabData.push({
      title: 'Wymiary i masa',
      icon: 'person-shirt',
      label: labelMap[key] || key,
      value: value || 'Opis właściwości',
    });
  });

  // Koła i zawieszenie
  Object.entries(data.wheelsSuspension).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      frontWheelSize: 'Rozmiar felg przednich',
      rearWheelSize: 'Rozmiar felg tylnych',
      frontTireSize: 'Rozmiar opon przednich',
      rearTireSize: 'Rozmiar opon tylnych',
      frontSuspension: 'Zawieszenie przednie',
      rearSuspension: 'Zawieszenie tylne',
      frontBrakes: 'Hamulce przednie',
      rearBrakes: 'Hamulce tylne',
      steeringType: 'Typ kierownicy',
    };

    tabData.push({
      title: 'Koła i zawieszenie',
      icon: 'network-connection',
      label: labelMap[key] || key,
      value: value || 'Opis właściwości',
    });
  });

  // Zużycie paliwa i emisje
  Object.entries(data.fuelEmission).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      fuelConsumptionCity: 'Zużycie paliwa w mieście',
      fuelConsumptionHighway: 'Zużycie paliwa poza miastem',
      fuelConsumptionCombined: 'Zużycie paliwa łącznie',
      co2Emission: 'Emisja CO2',
      emissionStandard: 'Norma emisji spalin',
      fuelTankCapacity: 'Pojemność zbiornika paliwa',
      range: 'Zasięg',
    };

    tabData.push({
      title: 'Zużycie paliwa i emisje',
      icon: 'gas',
      label: labelMap[key] || key,
      value: value || 'Opis właściwości',
    });
  });

  // Inne informacje
  Object.entries(data?.other || {}).forEach(([key, value]) => {
    const labelMap: Record<string, string> = {
      airConditioning: 'Klimatyzacja',
      heatingSystem: 'System ogrzewania',
      soundSystem: 'System audio',
      navigationSystem: 'System nawigacji',
      keylessEntry: 'Dostęp bezkluczykowy',
      startSystem: 'System uruchamiania',
      warranty: 'Gwarancja',
      serviceInterval: 'Interwał serwisowy',
    };

    tabData.push({
      title: 'Inne informacje',
      icon: 'i',
      label: labelMap[key] || key,
      value: value || 'Opis właściwości',
    });
  });

  return tabData;
};

export const RaportHSPSpecs: React.FC<RaportHSPSpecsProps> = ({ data }) => {
  const tabData = convertSpecsToTabData(data);

  return (
    <div className="mt-10 space-y-8">
      <Tabs
        data={tabData}
        defaultValue="silnik-i-osiągi"
        className="mt-6"
        contentSize="wide"
        prefix="specs"
      />
    </div>
  );
};
